import { PropTypes } from 'prop-types';
import React, { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAnalyticsEnabled,
  selectPermissions,
  selectRole,
  selectSite,
  selectUserGroup,
  selectUserName,
} from '../../store/authReducer';
import { selectModal } from '../../store/modal';
import { logout } from '../../views/login/actions';
import { ModalRoot } from './ModalRoot';
import DetergentModalBridge from '../../views/settings/components/ManageDetergents/DetergentModalBridge';
import Navbar from './Nav';
import SideNav from './SideNav';
import ZendeskSupportWidget from './ZendeskSupportWidget';
import { getSideMenuItems } from '../navItems';
import {
  ANALYTICS_RESTRUCTURE_FLAG,
  ANALYTICS_RESTRUCTURE_FLAG_DEFAULT,
} from '../../views/analytics/models';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

const MOBILE_WIDTH = 700;
// NOTE: could load theme from context,
const Main = ({ children }) => {
  const dispatch = useDispatch();
  const [isNavOpen, setNavState] = React.useState(window.innerWidth >= MOBILE_WIDTH);

  // Select state values from Redux store
  const selectedSite = useSelector(selectSite);
  const siteId = selectedSite ? selectedSite.toString() : null;
  const modal = useSelector(selectModal);
  const username = useSelector(selectUserName);
  const analyticsToggle = useSelector(selectAnalyticsEnabled);
  const permissions = useSelector(selectPermissions);
  const userGroup = useSelector(selectUserGroup);

  // Create action dispatchers
  const handleLogout = useCallback(() => dispatch(logout()), [dispatch]);

  // Memoize base URL to avoid string concatenation on every render
  const baseUrl = useMemo(() => `/${siteId}`, [siteId]);

  const tasksV2Enabled = useFeatureFlag('tasks-v2', true);
  const claimsOldEnabled = useFeatureFlag('claims-old', true);
  const myAssetsEnabled = useFeatureFlag('my-assets', true);
  const analyticsRestructureEnabled = useFeatureFlag(
    ANALYTICS_RESTRUCTURE_FLAG,
    ANALYTICS_RESTRUCTURE_FLAG_DEFAULT
  );

  // Memoize side menu items to avoid recreation on every render
  const sideMenuItems = useMemo(() => {
    let items = getSideMenuItems(baseUrl);
    if (!tasksV2Enabled) {
      items = items.filter((item) => item.label !== 'tasksV2');
    }
    if (!claimsOldEnabled) {
      items = items.filter((item) => item.label !== 'claimsOld');
    }
    if (claimsOldEnabled) {
      items = items.filter((item) => item.label !== 'claimsV2');
    }
    if (!myAssetsEnabled) {
      items = items.map((item) =>
        item.label === 'inventory'
          ? {
              ...item,
              subItems: item.subItems?.filter(
                (sub) => !['myAssets', 'siteAssets'].includes(sub.label)
              ),
            }
          : item
      );
    }
    if (!analyticsRestructureEnabled) {
      items = items.map((item) =>
        item.label === 'analytics' ? { ...item, subItems: undefined } : item
      );
    }
    return items;
  }, [baseUrl, tasksV2Enabled, claimsOldEnabled, myAssetsEnabled, analyticsRestructureEnabled]);

  return (
    <>
      <div className="app-main">
        <SideNav
          homeUrl="/"
          menuItems={sideMenuItems}
          username={username}
          analyticsEnabled={analyticsToggle}
          permissions={permissions}
          setNavState={setNavState}
          isOpen={isNavOpen}
          userGroup={userGroup}
          mobileBreakpoint={MOBILE_WIDTH}
          logout={handleLogout}
        />
        <main>
          <Navbar
            username={username}
            siteId={siteId}
            setNavState={setNavState}
            isNavOpen={isNavOpen}
          />
          <div className="page-container">{children}</div>
        </main>
      </div>
      <DetergentModalBridge />
      <ModalRoot {...modal} />
    </>
  );
};

Main.propTypes = {
  children: PropTypes.node,
};

export default Main;
